import { addDays, compareISO, diasEntre, inicioDeMes, inicioDeSemana } from './calendar';
import { fechaObs } from './freshness';
import {
  cambioPctExacto,
  clasificarCambioExacto,
  mean,
  median,
  pctChange,
  restaExacta,
  roundTo,
} from './numbers';
import type { Calidad, Historico, ISODate, PuntoHistorico, Tendencia, Variacion } from './types';

/**
 * Estadística de series de precios. La variación usa LA MISMA semántica que el servidor
 * (docs/CONTRATO_API.md §2.8) para que el modo básico (cálculo local) y el completo coincidan:
 *  - fecha de observación = fechaFin ?? fecha,
 *  - actual = prom de la observación más reciente,
 *  - previo = prom de la observación más reciente con fechaObs ≤ fechaActual − periodo; si no existe pero la
 *    serie abarca ≥ periodo/2 días, la más antigua (resultado "aproximado"); si no, sin dato,
 *  - tendencia: sube si Δ% ≥ 1, baja si ≤ −1, estable en otro caso (Δ% redondeado a 2 decimales),
 *  - spark: hasta 12 promedios más recientes, en orden cronológico.
 * Los datos SIMULADOS (demostración) nunca se mezclan con reales: si hay reales, se ignoran los simulados.
 */
export const UMBRAL_TENDENCIA_PCT = 1;
export const MAX_SPARK = 12;

type ConFecha = { fecha: ISODate; fechaFin?: ISODate | null };
export type PuntoPrecio = ConFecha & { prom: number; calidad?: Calidad };
export type PuntoRango = ConFecha &
  Pick<PuntoHistorico, 'min' | 'max' | 'prom' | 'minBs' | 'maxBs' | 'promBs'> & {
    calidad?: Calidad;
  };

const obs = (p: ConFecha): ISODate => fechaObs(p);
const cronologico = <T extends ConFecha>(a: T, b: T): number =>
  compareISO(obs(a), obs(b)) || compareISO(a.fecha, b.fecha);

/** Si hay datos reales descarta los simulados; si todo es simulado (demostración) los conserva. */
export function preferirReales<T extends { calidad?: Calidad }>(puntos: readonly T[]): T[] {
  const reales = puntos.filter((p) => !p.calidad?.simulado);
  return reales.length > 0 ? reales : [...puntos];
}

export function clasificarTendencia(difPct: number | null | undefined): Tendencia {
  if (difPct === null || difPct === undefined || !Number.isFinite(difPct)) return 'sin_dato';
  if (difPct >= UMBRAL_TENDENCIA_PCT) return 'sube';
  return difPct <= -UMBRAL_TENDENCIA_PCT ? 'baja' : 'estable';
}

export interface CalculoVariacion {
  actual: number;
  fechaActual: ISODate;
  previo: number | null;
  fechaPrevio: ISODate | null;
  difAbs: number | null;
  difPct: number | null;
  tendencia: Tendencia;
  /** Observaciones de la serie. */
  puntos: number;
  spark: number[];
  /** El previo salió de la observación más antigua porque la serie no cubre el período completo. */
  aproximado: boolean;
}

function elegirPrevio<T extends PuntoPrecio>(
  ordenados: T[],
  actual: T,
  periodoDias: number,
): { fila: T; aproximado: boolean } | null {
  const limite = addDays(obs(actual), -periodoDias);
  if (limite === null) return null;
  const previos = ordenados.filter((p) => obs(p) <= limite);
  const exacto = previos.at(-1);
  if (exacto) return { fila: exacto, aproximado: false };
  const masAntigua = ordenados[0];
  if (!masAntigua || obs(masAntigua) >= obs(actual)) return null;
  const cubre = diasEntre(obs(masAntigua), obs(actual)) >= periodoDias / 2;
  return cubre ? { fila: masAntigua, aproximado: true } : null;
}

/** Variación de una serie (todas las filas de UNA región y fuente). null si no hay ningún punto válido. */
export function calcularVariacion(
  puntos: readonly PuntoPrecio[],
  periodoDias: number,
): CalculoVariacion | null {
  // Igual que el servidor: un promedio ≤ 0 es un error de captura, no un precio.
  const validos = puntos.filter((p) => Number.isFinite(p.prom) && p.prom > 0);
  const ordenados = preferirReales(validos).sort(cronologico);
  const actual = ordenados.at(-1);
  if (!actual) return null;
  const previo = elegirPrevio(ordenados, actual, periodoDias);
  const difPct = previo ? cambioPctExacto(actual.prom, previo.fila.prom, 2) : null;
  const clase = previo
    ? clasificarCambioExacto(actual.prom, previo.fila.prom, UMBRAL_TENDENCIA_PCT)
    : null;
  return {
    actual: actual.prom,
    fechaActual: obs(actual),
    previo: previo?.fila.prom ?? null,
    fechaPrevio: previo ? obs(previo.fila) : null,
    difAbs: previo ? restaExacta(actual.prom, previo.fila.prom, 4) : null,
    difPct,
    tendencia: clase ?? 'sin_dato',
    puntos: ordenados.length,
    spark: ordenados.slice(-MAX_SPARK).map((p) => p.prom),
    aproximado: previo?.aproximado ?? false,
  };
}

/* ───────────── Variaciones desde históricos (modo básico) ───────────── */

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Orden del servidor: |Δ%| descendente, sin dato al final, y desempate por identidad de la serie. */
export const ordenVariaciones = (a: Variacion, b: Variacion): number =>
  Number(a.difPct === null) - Number(b.difPct === null) ||
  Math.abs(b.difPct ?? 0) - Math.abs(a.difPct ?? 0) ||
  cmp(a.serie.rubro, b.serie.rubro) ||
  cmp(a.serie.categoria, b.serie.categoria) ||
  cmp(a.serie.presentacion ?? '', b.serie.presentacion ?? '') ||
  cmp(a.fuente ?? '', b.fuente ?? '') ||
  cmp(a.region ?? '', b.region ?? '');

/**
 * Agrupa por (rubro, categoría, presentación, región, fuente) y calcula la variación de cada serie.
 * Las series sin categoría (históricos mezclados) se omiten: no hay a qué serie atribuirlas.
 * Orden: mayor |Δ%| primero; las series sin variación van al final.
 */
export function variacionesDesdeHistoricos(
  historicos: readonly Historico[],
  periodoDias: number,
): Variacion[] {
  const resultado: Variacion[] = [];
  for (const h of historicos) {
    if (!h.categoria) continue;
    const grupos = new Map<string, PuntoHistorico[]>();
    for (const p of h.puntos) {
      const clave = `${p.region ?? ''}|${p.fuente ?? ''}`;
      grupos.set(clave, [...(grupos.get(clave) ?? []), p]);
    }
    for (const puntos of grupos.values()) {
      const calculo = calcularVariacion(puntos, periodoDias);
      const primero = puntos[0];
      if (!calculo || !primero) continue;
      const { aproximado: _aproximado, ...datos } = calculo;
      resultado.push({
        serie: { rubro: h.rubro, categoria: h.categoria, presentacion: h.presentacion },
        region: primero.region,
        fuente: primero.fuente,
        unidad: h.unidad ?? 'kg',
        periodoDias,
        ...datos,
      });
    }
  }
  return resultado.sort(ordenVariaciones);
}

/* ───────────── Agregación y resumen ───────────── */

export type Granularidad = 'dia' | 'semana' | 'mes';

export interface PuntoAgregado {
  /** Inicio del período (el día, el lunes de la semana o el día 1 del mes). */
  fecha: ISODate;
  min: number;
  max: number;
  prom: number;
  minBs: number | null;
  maxBs: number | null;
  promBs: number | null;
  /** Observaciones agrupadas. */
  n: number;
}

const claveDePeriodo = (iso: ISODate, g: Granularidad): ISODate | null =>
  g === 'dia' ? iso : g === 'semana' ? inicioDeSemana(iso) : inicioDeMes(iso);

const todosNumeros = (valores: Array<number | null>): valores is number[] =>
  valores.every((v) => v !== null);

function agregado(fecha: ISODate, grupo: PuntoRango[]): PuntoAgregado {
  const bs = (campo: 'minBs' | 'maxBs' | 'promBs'): number[] | null => {
    const valores = grupo.map((p) => p[campo]);
    return todosNumeros(valores) ? valores : null;
  };
  const minBs = bs('minBs');
  const maxBs = bs('maxBs');
  const promBs = bs('promBs');
  return {
    fecha,
    min: Math.min(...grupo.map((p) => p.min)),
    max: Math.max(...grupo.map((p) => p.max)),
    prom: roundTo(mean(grupo.map((p) => p.prom)) ?? 0, 6),
    minBs: minBs ? Math.min(...minBs) : null,
    maxBs: maxBs ? Math.max(...maxBs) : null,
    promBs: promBs ? roundTo(mean(promBs) ?? 0, 4) : null,
    n: grupo.length,
  };
}

/** Une observaciones por día, semana (lunes) o mes: min de mínimos, max de máximos, promedio de promedios. */
export function agregarPorPeriodo(
  puntos: readonly PuntoRango[],
  granularidad: Granularidad,
): PuntoAgregado[] {
  const grupos = new Map<ISODate, PuntoRango[]>();
  for (const p of [...puntos].sort(cronologico)) {
    const clave = claveDePeriodo(obs(p), granularidad);
    if (clave) grupos.set(clave, [...(grupos.get(clave) ?? []), p]);
  }
  return [...grupos].map(([fecha, grupo]) => agregado(fecha, grupo));
}

export interface ResumenEstadistico {
  n: number;
  min: number;
  max: number;
  prom: number;
  mediana: number;
  primero: number;
  ultimo: number;
  difAbs: number;
  difPct: number | null;
  desde: ISODate;
  hasta: ISODate;
}

/** Mín, máx, promedio, mediana y cambio entre la primera y la última observación. null si no hay puntos. */
export function resumenEstadistico(puntos: readonly PuntoRango[]): ResumenEstadistico | null {
  const ordenados = preferirReales(puntos.filter((p) => Number.isFinite(p.prom))).sort(cronologico);
  const primero = ordenados[0];
  const ultimo = ordenados.at(-1);
  if (!primero || !ultimo) return null;
  const proms = ordenados.map((p) => p.prom);
  const cambio = pctChange(ultimo.prom, primero.prom);
  return {
    n: ordenados.length,
    min: Math.min(...ordenados.map((p) => p.min)),
    max: Math.max(...ordenados.map((p) => p.max)),
    prom: roundTo(mean(proms) ?? 0, 6),
    mediana: median(proms) ?? 0,
    primero: primero.prom,
    ultimo: ultimo.prom,
    difAbs: roundTo(ultimo.prom - primero.prom, 4),
    difPct: cambio === null ? null : roundTo(cambio, 2),
    desde: obs(primero),
    hasta: obs(ultimo),
  };
}

export type RangoTiempo = '7d' | '30d' | '90d' | '1a' | 'todo';

const DIAS_RANGO: Record<Exclude<RangoTiempo, 'todo'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1a': 365,
};

/** Fecha inicial de un rango relativo a `hoy` (null = sin límite inferior). */
export function rangoDesde(rango: RangoTiempo, hoy: ISODate): ISODate | null {
  return rango === 'todo' ? null : addDays(hoy, -DIAS_RANGO[rango]);
}
